import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Users, Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIER_NAMES = { legacy: "Legacy", impact: "Impact", courage: "Courage", honor: "Honor", tribute: "Hero Team" };

export const CapacityCard = ({ token }) => {
  const headers = { "X-Admin-Token": token };
  const [cap, setCap] = useState(null);
  const [edit, setEdit] = useState({ max_players: "", max_teams: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/admin/capacity`, { headers });
    setCap(res.data);
    setEdit({ max_players: res.data.max_players, max_teams: res.data.max_teams });
  };
  useEffect(() => { load().catch(() => toast.error("Could not load capacity.")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/capacity`, { max_players: Number(edit.max_players), max_teams: Number(edit.max_teams) }, { headers });
      setCap(res.data);
      toast.success("Capacity limits saved.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save capacity.");
    } finally {
      setSaving(false);
    }
  };

  if (!cap) return null;
  const b = cap.breakdown;
  const pct = Math.min(100, Math.round((cap.players_used / cap.max_players) * 100));
  const inputCls = "w-24 border-b-2 border-border bg-transparent px-1 py-1.5 text-espresso rounded-none focus:outline-none focus:border-rust text-sm";

  return (
    <div data-testid="admin-capacity-card" className="mt-8 border border-border bg-background p-6 hard-shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
        <div className="flex-1">
          <h3 className="label-font text-xs text-rust tracking-[0.15em]">Tournament Capacity</h3>
          <div className="mt-4 flex flex-wrap items-end gap-x-10 gap-y-3">
            <div>
              <div className="flex items-center gap-2 text-muted-foreground text-xs label-font"><Users size={13} /> Golfers</div>
              <div data-testid="capacity-players" className="font-serif font-black text-3xl text-espresso">{cap.players_used} <span className="text-base text-muted-foreground font-bold">/ {cap.max_players}</span></div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-muted-foreground text-xs label-font"><Flag size={13} /> Teams</div>
              <div data-testid="capacity-teams" className="font-serif font-black text-3xl text-espresso">{cap.teams_used} <span className="text-base text-muted-foreground font-bold">/ {cap.max_teams}</span></div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs label-font">Spots Remaining</div>
              <div data-testid="capacity-remaining" className={`font-serif font-black text-3xl ${cap.sold_out ? "text-rust" : "text-espresso"}`}>{cap.sold_out ? "Sold Out" : cap.players_remaining}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs label-font">Waitlist</div>
              <div data-testid="capacity-waitlist" className="font-serif font-black text-3xl text-espresso">{cap.waitlist_count}</div>
            </div>
          </div>
          <div className="mt-4 h-2 bg-espresso/10 w-full"><div className="h-2 bg-rust transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5 text-sm text-muted-foreground" data-testid="capacity-breakdown">
            <div>Paid registrations: <span className="text-espresso font-medium">{b.paid_registration_players} golfers</span></div>
            <div>Pending card checkouts (&lt;24h): <span className="text-espresso font-medium">{b.pending_checkout_players}</span></div>
            <div>Invoice / check pledges: <span className="text-espresso font-medium">{b.invoice_pledge_players}</span></div>
            <div>Team registrations: <span className="text-espresso font-medium">{b.team_registrations}</span> · Individuals: <span className="text-espresso font-medium">{b.individual_golfers}</span></div>
            <div>Sponsor golfers: <span className="text-espresso font-medium">{b.sponsor_players}</span> ({Object.entries(b.sponsor_by_tier).filter(([, v]) => v.count).map(([k, v]) => `${TIER_NAMES[k]} ×${v.count}`).join(", ") || "none"})</div>
            <div>Hero Teams sponsored: <span className="text-espresso font-medium">{b.hero_teams}</span></div>
          </div>
        </div>
        <div className="border-t lg:border-t-0 lg:border-l border-border pt-5 lg:pt-0 lg:pl-6 shrink-0">
          <div className="label-font text-[10px] text-espresso/70">Limits</div>
          <div className="mt-2 flex items-end gap-4">
            <label className="text-xs text-muted-foreground">Max golfers<br /><input data-testid="capacity-max-players" type="number" min="4" className={inputCls} value={edit.max_players} onChange={(e) => setEdit({ ...edit, max_players: e.target.value })} /></label>
            <label className="text-xs text-muted-foreground">Max teams<br /><input data-testid="capacity-max-teams" type="number" min="1" className={inputCls} value={edit.max_teams} onChange={(e) => setEdit({ ...edit, max_teams: e.target.value })} /></label>
          </div>
          <button data-testid="capacity-save-btn" onClick={save} disabled={saving} className="mt-4 label-font text-[11px] bg-espresso text-cream px-4 py-2.5 border border-espresso flex items-center gap-2 hard-shadow-sm disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Limits
          </button>
        </div>
      </div>
    </div>
  );
};
